import React from 'react';

import DataCatalogItemDetails from 'data-catalog/DataCatalogItemDetails';
import DataCatalogOrigin from 'data-catalog/DataCatalogOrigin';
import {performSignedFileQuery} from 'external-files/ExternalFileUtils';
import {updateFile} from 'files/network/FileDAO';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {renderFilePreview} from 'pipelines/files/ExternalFilePaneContent';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {Title} from 'ui-library/Headers';
import {formatBytes} from 'util/NameUtil';

import {FileViewQuery} from './__generated/FileViewQuery';

type File = ExtractByTypename<FileViewQuery['file'], 'FileNode'>;

interface FileDetailsProps {
  file: File;
  reload: () => void;
}

export default function FileDetails({file, reload}: FileDetailsProps): JSX.Element {
  const extraColumns = [
    <>
      <Title>Benchling File ID</Title>
      {file.displayId}
    </>,
    <>
      <Title>Size</Title>
      {file.scratchFile.size ? formatBytes(file.scratchFile.size) : ''}
    </>,
    <>
      <Title>Origin</Title>
      <DataCatalogOrigin originType={file.originType} lineageParent={file.lineageParent} />
    </>,
    <>
      <Title>Original File Name</Title>
      {file.scratchFile.filename}
    </>,
  ];

  const renderDownloadButton = (): JSX.Element => (
    <Tooltip container="body" placement={TooltipPlacement.TOP} title="Download original file">
      <Button
        buttonStyle={ButtonStyle.SECONDARY}
        className="metadata-header-button-spacing-left"
        icon="download"
        disabled={file.scratchFile.externalFileId == null || file.scratchFile.downloadUrl == null}
        onClick={async () => {
          const signedURL = await performSignedFileQuery(
            file.scratchFile.externalFileId!,
            file.scratchFile.downloadUrl!,
            file.id
          );
          if (signedURL == null) {
            return;
          }
          window.open(signedURL, '_blank', 'noopener noreferrer');
        }}
        size={ButtonSize.SMALL}
      />
    </Tooltip>
  );

  return (
    <DataCatalogItemDetails
      dataCatalogItem={file}
      extraColumns={extraColumns}
      tagsEmptyMessage="This file has no custom fields."
      onChange={async (updateParams: {
        id: string;
        name?: string;
        folderId?: string;
        customFields?: {[name: string]: string};
      }): Promise<void> => {
        await updateFile(updateParams);
        reload();
      }}
      renderDownloadButton={renderDownloadButton}
    >
      {renderFilePreview(file.scratchFile)}
    </DataCatalogItemDetails>
  );
}
